import { Checkbox as CheckboxPrimitive } from "@base-ui/react/checkbox";
import { CheckboxIcon, CheckIcon } from "@solomon-labs/design/components/icons";
import * as React from "react";

import { cn } from "../../utils/cn";

interface CheckboxProps extends CheckboxPrimitive.Root.Props {
    /** Demo affordance — force the hover VISUAL (darker outline) on with no
     *  pointer present, for scripted sims. Emits the same color the real
     *  `hover:` curve produces. `checked` stays prop-driven (the Base UI
     *  `checked` prop). Default `false` — existing callers unaffected. */
    forceHover?: boolean;
    /** Demo affordance — force a focus VISUAL (ring) on with no keyboard
     *  focus present. Reads distinctly from hover. Default `false`. */
    forceFocus?: boolean;
}

function Checkbox({
    className,
    forceHover = false,
    forceFocus = false,
    ...props
}: CheckboxProps): React.ReactElement {
    return (
        <CheckboxPrimitive.Root
            data-slot="checkbox"
            className={cn(
                // The box glyph carries the outline; the check glyph layers on
                // top via the Indicator, so each can animate independently and a
                // single box serves every state. Color flows through
                // `currentColor`, so state-driven text utilities tint the box
                // outline and the check together.
                "group relative inline-flex size-6 shrink-0 items-center justify-center transition-colors",
                "text-line-dark hover:text-charcoal-700",
                // Touch target padding via after: pseudo-element so the tap area
                // is comfortably larger than the visual.
                "after:absolute after:-inset-x-3 after:-inset-y-2",
                // Base UI surfaces the disabled state via both the HTML
                // `disabled` attribute AND `data-disabled`. Cover both so the
                // hover/cursor/color overrides land regardless of which Tailwind
                // matches first. `pointer-events-none` is the load-bearing piece
                // that prevents `:hover` from triggering at all.
                "disabled:pointer-events-none disabled:cursor-not-allowed disabled:text-line-neutral",
                "data-disabled:pointer-events-none data-disabled:cursor-not-allowed data-disabled:text-line-neutral",
                "aria-invalid:text-error-base",
                "data-checked:text-success-base",
                // Forced demo hover/focus (sim): literal classes so the box
                // outline darkens / a focus ring paints with no pointer. Order
                // after the base color so tailwind-merge resolves in favor of
                // the forced tone; `data-checked` still wins when checked.
                forceHover && "text-charcoal-700",
                forceFocus && "text-charcoal-500 ring-2 ring-charcoal-500/40",
                className
            )}
            {...props}
        >
            {/* Box outline — always rendered underneath. */}
            <CheckboxIcon className="size-full" />
            {/* Check mark — layered on top, shown only when checked. */}
            <CheckboxPrimitive.Indicator
                data-slot="checkbox-indicator"
                className="absolute inset-0 flex items-center justify-center"
            >
                <CheckIcon className="size-3.5" />
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    );
}

export { Checkbox };
